import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { useEffect, useState, type ReactNode, type FormEvent } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, ChevronRight, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/controls/ThemeToggle";
import { LanguageSwitcher } from "@/components/controls/LanguageSwitcher";
import { CurrencySwitcher } from "@/components/controls/CurrencySwitcher";
import { PageTransition } from "@/components/motion/PageTransition";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Logo } from "@/components/brand/Logo";
import { PageBanner } from "@/components/layout/PageBanner";
import { DiagnosticBanner } from "@/components/marketing/DiagnosticBanner";

export function PublicShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Strip locale prefix (/en, /fr, ...) for active comparisons.
  const localeStripped = pathname.replace(/^\/[a-z]{2}(?=\/|$)/, "") || "/";
  const isActive = (to: string) => (to === "/" ? localeStripped === "/" : localeStripped === to || localeStripped.startsWith(to + "/"));
  const mobileItemCls = (to: string) =>
    cn(
      "rounded-md px-3 py-2 transition-colors",
      isActive(to)
        ? "bg-primary/10 text-primary"
        : "text-text-2 hover:bg-surface-2 hover:text-text-1",
    );

  async function onNewsletterSubmit(e: FormEvent) {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(newsletterEmail)) {
      toast({ title: t("footer.newsletter.invalid", "Enter a valid email"), variant: "destructive" });
      return;
    }
    setNewsletterLoading(true);
    const { error } = await supabase.from("leads").insert({
      source: "newsletter",
      email: newsletterEmail,
      payload: { email: newsletterEmail } as never,
    });
    setNewsletterLoading(false);
    if (error) {
      toast({ title: t("footer.newsletter.error", "Could not subscribe — try again"), variant: "destructive" });
    } else {
      toast({ title: t("footer.newsletter.success", "You're in. Welcome aboard.") });
      setNewsletterEmail("");
    }
  }

  // Standard brand lockup size — same in header and footer. Width-anchored
  // because the 2026 lockup is a square stacked artwork (boot + wordmark).
  const LogoMark = ({ className = "w-36 h-auto" }: { className?: string }) => (
    <Logo className={className} asLink={false} />
  );

  const servicesItems = [
    { to: "/services", title: t("pages.navServices.overview", "All services"), desc: t("pages.navServices.overviewDesc", "Overview of every Bootpreneurs service") },
    { to: "/marketplace", title: t("pages.navServices.marketplace", "Marketplace"), desc: t("pages.navServices.marketplaceDesc", "Browse vetted coaches, mentors & fractionals") },
    { to: "/services/coaching", title: t("pages.navServices.coaching"), desc: t("pages.navServices.coachingDesc") },
    { to: "/mentoring", title: t("pages.navServices.mentoring"), desc: t("pages.navServices.mentoringDesc") },
    { to: "/fractional", title: t("pages.navServices.fractional"), desc: t("pages.navServices.fractionalDesc") },
    { to: "/services/microservices", title: t("pages.navServices.microservices"), desc: t("pages.navServices.microservicesDesc") },
    { to: "/services/investor-readiness", title: t("pages.navServices.investorReadiness", "Investor readiness"), desc: t("pages.navServices.investorReadinessDesc", "Get fundraise-ready in 8 weeks") },
    { to: "/services/retainers", title: t("pages.navServices.retainers", "Retainers"), desc: t("pages.navServices.retainersDesc", "Ongoing coaching & fractional support") },
    { to: "/enterprise", title: t("pages.navServices.enterprise", "For enterprise"), desc: t("pages.navServices.enterpriseDesc", "Custom programs for teams & accelerators") },
    { to: "/teams", title: t("pages.navServices.teams", "Bootpreneurs for Teams"), desc: t("pages.navServices.teamsDesc", "Custom cohorts for accelerators, banks & CVCs") },
  ];

  return (
    <div className="flex min-h-screen w-full flex-col bg-surface-1 text-text-1">
      <header
        data-scrolled={scrolled}
        className={cn(
          "sticky top-0 z-40 border-b border-border-1 transition-colors",
          scrolled ? "bg-surface-1 shadow-sm" : "bg-surface-1/85 backdrop-blur",
        )}
      >
        <div className="container flex h-20 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <LogoMark className="w-24 sm:w-36 h-auto" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:block">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuLink asChild className="inline-flex h-10 items-center px-3 text-sm text-text-3 transition-colors hover:text-text-1">
                    <Link to="/">{t("nav.home")}</Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild className="inline-flex h-10 items-center px-3 text-sm text-text-3 transition-colors hover:text-text-1">
                    <Link to="/catalog">{t("nav.bootcamps")}</Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild className="inline-flex h-10 items-center px-3 text-sm font-semibold text-primary transition-colors hover:text-text-1">
                    <Link to="/business-validator">{t("nav.businessValidator", "Business Validator")}</Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent text-text-3 hover:bg-transparent hover:text-text-1 data-[state=open]:bg-transparent data-[active]:bg-transparent">
                    {t("pages.navServices.label")}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[560px] gap-2 p-3 sm:grid-cols-2">
                      {servicesItems.map((it) => (
                        <li key={it.to}>
                          <NavigationMenuLink asChild>
                            <Link to={it.to} className="block rounded-md p-3 transition hover:bg-surface-2">
                              <div className="text-sm font-semibold leading-none text-text-1">{it.title}</div>
                              <p className="mt-1 text-xs leading-snug text-text-3">{it.desc}</p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild className="inline-flex h-10 items-center px-3 text-sm text-text-3 transition-colors hover:text-text-1">
                    <Link to="/about">{t("nav.about")}</Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild className="inline-flex h-10 items-center px-3 text-sm text-text-3 transition-colors hover:text-text-1">
                    <Link to="/careers">{t("nav.careers")}</Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild className="inline-flex h-10 items-center px-3 text-sm text-text-3 transition-colors hover:text-text-1">
                    <Link to="/pricing">{t("nav.pricing")}</Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild className="inline-flex h-10 items-center px-3 text-sm text-text-3 transition-colors hover:text-text-1">
                    <Link to="/downloads">{t("nav.playbooks")}</Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </nav>

          <div className="flex items-center gap-1">
            <CurrencySwitcher />
            <LanguageSwitcher />
            <ThemeToggle />
            <Button asChild variant="brand" size="sm" className="ml-2 hidden md:inline-flex">
              <Link to="/login">{t("common.signIn")}</Link>
            </Button>

            {/* Mobile menu */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] max-w-sm bg-surface-1">
                <SheetHeader>
                  <SheetTitle>{t("nav.home")}</SheetTitle>
                </SheetHeader>
                <nav className="mt-6 flex flex-col gap-1 text-sm">
                  <SheetClose asChild>
                    <Link to="/" className={mobileItemCls("/")}>{t("nav.home")}</Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/catalog" className={mobileItemCls("/catalog")}>{t("nav.bootcamps")}</Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/business-validator" className={mobileItemCls("/business-validator")}>{t("nav.businessValidator", "Business Validator")}</Link>
                  </SheetClose>
                  <Collapsible open={mobileServicesOpen} onOpenChange={setMobileServicesOpen}>
                    <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-text-2 transition-colors hover:bg-surface-2 hover:text-text-1">
                      <span>{t("pages.navServices.label")}</span>
                      <ChevronRight className={`h-4 w-4 text-text-3 transition-transform ${mobileServicesOpen ? "rotate-90" : ""}`} />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-1 ml-3 flex flex-col gap-1 border-l border-border-1 pl-3">
                      {servicesItems.map((it) => (
                        <SheetClose asChild key={it.to}>
                          <Link to={it.to} className={mobileItemCls(it.to)}>{it.title}</Link>
                        </SheetClose>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>

                  <SheetClose asChild>
                    <Link to="/about" className={mobileItemCls("/about")}>{t("nav.about")}</Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/careers" className={mobileItemCls("/careers")}>{t("nav.careers")}</Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/pricing" className={mobileItemCls("/pricing")}>{t("nav.pricing")}</Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/downloads" className={mobileItemCls("/downloads")}>{t("nav.playbooks")}</Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/community" className={mobileItemCls("/community")}>{t("nav.community")}</Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/support" className={mobileItemCls("/support")}>{t("nav.support")}</Link>
                  </SheetClose>
                  <div className="mt-4 border-t border-border-1 pt-4">
                    <SheetClose asChild>
                      <Button asChild variant="brand" size="lg" className="w-full"><Link to="/login">{t("common.signIn")}</Link></Button>
                    </SheetClose>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <PageBanner />
      <DiagnosticBanner />

      <main className="flex-1"><PageTransition>{children}</PageTransition></main>

      <footer className="border-t border-border-1 bg-surface-2/40">
        <div className="container py-12">
          {/* Newsletter strip */}
          <Card variant="feature" className="mb-10 flex flex-col items-start gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-primary" />
              <div>
                <div className="text-sm font-semibold text-text-1">{t("footer.newsletter.title", "Get the founder briefing")}</div>
                <div className="t-body-sm text-text-3">{t("footer.newsletter.subtitle", "1 email a week. Tactics, no fluff.")}</div>
              </div>
            </div>
            <form onSubmit={onNewsletterSubmit} className="flex w-full gap-2 sm:w-auto">
              <Input
                type="email"
                required
                placeholder={t("footer.newsletter.placeholder", "you@startup.com")}
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="h-9 w-full sm:w-64"
                aria-label={t("footer.newsletter.placeholder", "you@startup.com")}
              />
              <Button type="submit" variant="brand" size="sm" disabled={newsletterLoading}>
                {newsletterLoading ? t("common.loading") : t("footer.newsletter.cta", "Subscribe")}
              </Button>
            </form>
          </Card>

          <div className="grid grid-cols-2 gap-8 md:grid-cols-5">
            <div className="col-span-2 md:col-span-1">
              <div className="mb-3 max-w-[160px]"><LogoMark className="w-28 sm:w-36 h-auto" /></div>
              <p className="t-body-sm text-text-3">{t("footer.tagline")}</p>
            </div>
            <div>
              <div className="mb-3 t-eyebrow text-text-2">{t("footer.product")}</div>
              <ul className="space-y-2 t-body-sm text-text-3">
                <li><Link to="/catalog" className="transition-colors hover:text-text-1">{t("nav.bootcamps")}</Link></li>
                <li><Link to="/tracks" className="transition-colors hover:text-text-1">{t("footer.tracks", "Tracks")}</Link></li>
                <li><Link to="/services/coaching" className="transition-colors hover:text-text-1">{t("nav.coaching")}</Link></li>
                <li><Link to="/mentoring" className="transition-colors hover:text-text-1">{t("pages.navServices.mentoring")}</Link></li>
                <li><Link to="/mentors" className="transition-colors hover:text-text-1">{t("footer.mentors", "Browse mentors")}</Link></li>
                <li><Link to="/fractional" className="transition-colors hover:text-text-1">{t("nav.executives")}</Link></li>
                <li><Link to="/services/microservices" className="transition-colors hover:text-text-1">{t("pages.navServices.microservices")}</Link></li>
                <li><Link to="/downloads" className="transition-colors hover:text-text-1">{t("footer.downloads", "Downloads")}</Link></li>
                <li><Link to="/teams" className="transition-colors hover:text-text-1">{t("footer.teams", "Bootpreneurs for Teams")}</Link></li>
              </ul>
            </div>
            <div>
              <div className="mb-3 t-eyebrow text-text-2">{t("footer.resources")}</div>
              <ul className="space-y-2 t-body-sm text-text-3">
                <li><Link to="/blog" className="transition-colors hover:text-text-1">{t("nav.blog")}</Link></li>
                <li><Link to="/case-studies" className="transition-colors hover:text-text-1">{t("footer.caseStudies", "Case studies")}</Link></li>
                <li><Link to="/faq" className="transition-colors hover:text-text-1">{t("nav.faq")}</Link></li>
                <li><Link to="/community" className="transition-colors hover:text-text-1">{t("nav.community")}</Link></li>
                <li><Link to="/support" className="transition-colors hover:text-text-1">{t("nav.support")}</Link></li>
              </ul>
            </div>
            <div>
              <div className="mb-3 t-eyebrow text-text-2">{t("footer.company")}</div>
              <ul className="space-y-2 t-body-sm text-text-3">
                <li><Link to="/about" className="transition-colors hover:text-text-1">{t("nav.about")}</Link></li>
                <li><Link to="/enterprise" className="transition-colors hover:text-text-1">{t("nav.enterprise", "Enterprise")}</Link></li>
                <li><Link to="/careers" className="transition-colors hover:text-text-1">{t("nav.careers")}</Link></li>
                <li><Link to="/contact" className="transition-colors hover:text-text-1">{t("nav.contact")}</Link></li>
                <li><Link to="/contact?topic=partner" className="transition-colors hover:text-text-1">{t("footer.partners", "Partners")}</Link></li>
                <li><Link to="/apply" className="transition-colors hover:text-text-1">{t("footer.becomeProvider", "Become a coach / mentor")}</Link></li>
              </ul>
            </div>
            <div>
              <div className="mb-3 t-eyebrow text-text-2">{t("footer.legal")}</div>
              <ul className="space-y-2 t-body-sm text-text-3">
                <li><Link to="/legal/privacy" className="transition-colors hover:text-text-1">{t("footer.privacy", "Privacy")}</Link></li>
                <li><Link to="/legal/terms" className="transition-colors hover:text-text-1">{t("footer.terms", "Terms")}</Link></li>
                <li><Link to="/legal/refund-policy" className="transition-colors hover:text-text-1">{t("footer.refundPolicy", "Refund policy")}</Link></li>
                <li><Link to="/legal/dpa" className="transition-colors hover:text-text-1">{t("footer.dpa", "DPA")}</Link></li>
                <li>
                  <button
                    type="button"
                    onClick={() => document.dispatchEvent(new CustomEvent("bp:open-cookie-prefs"))}
                    className="transition-colors hover:text-text-1"
                  >
                    {t("footer.cookies")}
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-10 flex flex-col items-start justify-between gap-4 border-t border-border-1 pt-6 sm:flex-row sm:items-center">
            <p className="t-body-sm text-text-3">
              © {new Date().getFullYear()} Bootpreneurs · {t("footer.madeBy", "Made by founders, for founders")}
            </p>
            <LanguageSwitcher />
          </div>
        </div>
      </footer>
    </div>
  );
}
